import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef } from "@tanstack/react-table";
import Button from "components/base/Button";
import DatePicker from "components/base/DatePicker";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import LeadsFilterModal from "components/modals/LeadsFilterModal";
import { collaboratorBreadcrumbItems } from "data/commonData";
import axiosInstance from "helpers/api";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useState, useEffect } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import RevealDropdown, {
  RevealDropdownTrigger,
} from "components/base/RevealDropdown";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import AddCollaboratorModal from "components/modules/crm/collaborators/AddCollaboratorModal";
import CollaboratorsTable from "components/tables/CollaboratorsTable";
import EditCollaboratorModal from "components/modules/crm/collaborators/EditCollaboratorModal";
import _ from "lodash";
import Backdrop from "components/common/Backdrop";
import { atom, useAtom } from "jotai";
import NoDataFound from "components/common/NoDataFound";
import CollaboratorsSkeleton from "components/skeletons/CollaboratorsSkeleton";

interface UserInfo {
  email: string;
  payment_status: string;
}
const user_info = atom<UserInfo>({ email: "", payment_status: "" });

const Collaborators = () => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showCollaborators, setShowCollaborators] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;
  const collaboratorsTableColumns: ColumnDef<any>[] = [
    {
      accessorKey: "first_name",
      header: "Name",
      cell: ({ row: { original } }) => {
        const { invitee } = original;

        return (
          <div className="d-flex align-items-center">
            <div className="fs-8 fw-bold">
              {invitee.collaborator_first_name} {invitee.collaborator_last_name}
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: "25%" }, className: "ps-0" },
        cellProps: { className: "white-space-nowrap ps-0" },
      },
    },
    {
      accessorKey: "email",
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-success-subtle rounded me-2">
              <FeatherIcon
                icon="mail"
                size={16}
                className="text-success-dark"
              />
            </div>
            <span>Email</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { invitee } = original;
        return (
          <Link
            className="text-body-highlight"
            to={`mailto:${invitee.collaborator_email}`}
          >
            {invitee.collaborator_email}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: "15%" },
          className: "ps-4 pe-5 border-end border-translucent",
        },
        cellProps: {
          className:
            "white-space-nowrap fw-semibold ps-4 border-end border-translucent",
        },
      },
    },
    {
      accessorKey: "phone",
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-primary-subtle rounded me-2">
              <FeatherIcon
                icon="phone"
                size={16}
                className="text-primary-dark"
              />
            </div>
            <span>Phone</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { invitee } = original;
        return (
          <Link
            className="text-body-highlight"
            to={`tel:${invitee.collaborator_phone}`}
          >
            {invitee.collaborator_phone}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: "15%", minWidth: "180px" },
          className: "ps-4 pe-5 border-end border-translucent",
        },
        cellProps: {
          className:
            "white-space-nowrap fw-semibold ps-4 border-end border-translucent",
        },
      },
    },

    {
      accessorKey: "created_on",
      header: "Created date",
      cell: ({ row: { original } }) => {
        try {
          const date = new Date(original.created_on);
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: userTimezone,
          }).format(date);
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'Invalid date';
        }
      },

      meta: {
        headerProps: {
          style: { width: "15%" },
          className: "ps-4 pe-5",
        },
        cellProps: {
          className: "white-space-nowrap ps-4 text-body-tertiary",
        },
      },
    },
    {
      id: "collaboratorDropdown",
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              {/* <Dropdown.Item
                as={Link}
                to={`/leads/${original.id}`}
                eventKey="1"
              >
                View
              </Dropdown.Item> */}
              <Dropdown.Item
                // as={Link}
                // to={`/edit-lead/${original.id}`}
                onClick={() => handleShowEditModal(original.collaborator_id)}
                eventKey="2"
              >
                Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                eventKey="4"
                className="text-danger"
                onClick={() => handleShowDeleteModal(original.collaborator_id)}
              >
                Remove
              </Dropdown.Item>
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: "text-end pe-0 ps-4",
        },
      },
    },
  ];
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openAddCollaboratorModal, setOpenAddCollaboratorModal] =
    useState(false);
  const [openEditCollaboratorModal, setOpenEditCollaboratorModal] =
    useState(false);
  const [collaborators, setCollaborators] = useState([]);

  const fetchCollaborators = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/collaborators");
      if (response.data?.data) {
        setCollaborators(response.data.data);
      } else {
        console.error('Invalid response structure for collaborators:', response.data);
        setCollaborators([]);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch collaborators');
      setCollaborators([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollaborators();
  }, []);

  const fetchResults = async (searchQuery: string) => {
    try {
      const response = await axiosInstance.get(`/collaborators`, {
        params: { q: searchQuery },
      });
      if (response.data?.data?.collaborators) {
        setCollaborators(response.data.data.collaborators);
      } else {
        console.error('Invalid response structure for search results:', response.data);
        setCollaborators([]);
      }
    } catch (err) {
      toast.error("Error fetching search results");
      setCollaborators([]);
    }
  };

  const handleSearchInputChange = _.debounce((searchQuery: any) => {
    fetchResults(searchQuery.target.value);
  }, 500);

  const fetchLeadData = async () => {
    try {
      const response = await axiosInstance.get("/collaborators");
      if (response.data?.data?.collaborators) {
        setCollaborators(response.data.data.collaborators);
      } else {
        console.error('Invalid response structure for lead data:', response.data);
        setCollaborators([]);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch lead data');
      setCollaborators([]);
    }
  };

  const handleShowDeleteModal = (id: any) => {
    setShowDeleteModal(true);
    setSelectedLeadId(id);
  };

  const handleShowEditModal = (id: any) => {
    setOpenEditCollaboratorModal(true);
    setSelectedLeadId(id);
  };

  const handleDeleteLead = async (id: any) => {
    try {
      const response = await axiosInstance.delete(`/collaborators/${id}`);
      const filterLeadData = collaborators.filter(
        (item: any) => item.invitee.id !== id
      );
      setCollaborators(filterLeadData);
      setShowDeleteModal(false);
      toast.success(response.data?.message || 'Collaborator deleted successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete collaborator');
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    fetchLeadData();
  }, []);

  const table = useAdvanceTable({
    data: collaborators,
    columns: collaboratorsTableColumns,
    pageSize: 5,
    pagination: true,
    sortable: true,
    selection: false,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false,
      },
    },
  });

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowCollaborators(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  return (
    <>
      {(isAuthorized || showCollaborators) && (
        <div>
          {isLoading ? (
            <CollaboratorsSkeleton />
          ) : (
            <>
              <PageBreadcrumb items={collaboratorBreadcrumbItems} />

              <div className="mb-6">
                <AdvanceTableProvider {...table}>
                  <h2 className="mb-4">
                    {collaborators.length}{" "}
                    {collaborators.length > 1 ? "Collaborators" : "Collaborator"}
                  </h2>
                <Row className="g-3 justify-content-between mb-4">
                <Col xs="auto">
                  <Button
                      variant="primary"
                      className="me-4"
                      startIcon={
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                      }
                      onClick={() => setOpenAddCollaboratorModal(true)}
                  >
                      Create Collaborator
                  </Button>
                  {/* <Button
                  variant="link"
                  className="text-body px-0"
                  startIcon={
                    <FontAwesomeIcon
                      icon={faFileExport}
                      className="fs-9 me-2"
                    />
                  }
                >
                  Export
                </Button> */}
                </Col>
                <Col xs="auto">
                    <div className="d-flex">
                      <SearchBox
                        placeholder="Search"
                        className="me-2"
                        onChange={handleSearchInputChange}
                        prepend={<FeatherIcon icon="search" size={14} className="text-body-quaternary" />}
                    />
                      <DatePicker
                        defaultValue={new Date().toISOString()}
                        onChange={(dates: Date[]) => {}}
                    />
                      <Button
                        variant="phoenix-secondary"
                        className="px-3 ms-2"
                        onClick={() => setOpenFilterModal(true)}
                      >
                        <FontAwesomeIcon
                          icon={faFilter}
                          transform="down-3"
                          className="text-primary"
                        />
                      </Button>
                    </div>
                    <LeadsFilterModal
                      setLeads={setCollaborators}
                      show={openFilterModal}
                      handleClose={() => setOpenFilterModal(false)}
                  />
                </Col>
                </Row>
              {collaborators.length > 0 ? (
                <CollaboratorsTable />
              ) : (
                <NoDataFound type="Collaborators" customClass="mt-xl-15" />
              )}
            </AdvanceTableProvider>
          </div>
          <AddCollaboratorModal
            show={openAddCollaboratorModal}
            handleClose={() => setOpenAddCollaboratorModal(false)}
            setCollaborators={setCollaborators}
            collaborators={collaborators}
          />
          {openEditCollaboratorModal && (
            <EditCollaboratorModal
              selectedLeadId={selectedLeadId}
              show={openEditCollaboratorModal}
              handleClose={() => setOpenEditCollaboratorModal(false)}
              setCollaborators={setCollaborators}
              collaborators={collaborators}
            />
          )}

              <ConfirmDeleteModal
                modalShow={showDeleteModal}
                setModalShow={setShowDeleteModal}
                type="Collaborator"
                handleDelete={() => handleDeleteLead(selectedLeadId)}
              />
            </>
          )}
        </div>
      )}
      {!isAuthorized && <Backdrop />}
    </>
  );
};

export default Collaborators;
